import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { apiErrorCode } from "../lib/apiError";
import { useApiData } from "../lib/hooks";
// #2094 — `draft.saved_at` is a NAIVE datetime (the backend types it as a bare
// `dt.datetime`, UTC in practice, serialised with no offset). `parseUTC` reads
// it as the UTC instant it is; a bare `new Date()` would read it as LOCAL time
// and skew the AUTOSAVED label by the archer's whole UTC offset (cf. #2031).
import { parseUTC } from "../lib/dates";
import { useAuth } from "../lib/auth";
import { trackEvent } from "../lib/telemetry";
import {
  maxArrowScoreForFace,
  sumEnd,
  countXForRound,
  isMultiSpot,
  spotForPoint,
  applyMultiSpotRule,
} from "../lib/scoring";
import { nextWalkRoundId, pendingWalkRoundId, archersPendingOnEnd } from "../lib/balePending";
import { breakBoundaryInfo } from "../lib/segments";
import { toVbX, toVbY } from "../lib/targetGeometry";
import {
  ownRoundCommitted,
  baleMatesStillBehind,
  scoringRoles,
} from "../lib/baleProgress";
import { scoreStageDisplay, breakCuePayload } from "../lib/scoreSubtitle";
import { useScoreEntry } from "./useScoreEntry";
import { useAutosave, arrowPayload, autosaveContentKey } from "./useAutosave";
import { useBaleSession } from "./useBaleSession";
import { OPEN_ENDED_MAX_ENDS } from "../lib/roundConstants";

/**
 * Is this round DONE as far as the Score page is concerned — i.e. the server
 * will refuse any further end, so the page must render the finished state
 * (Close scoring) and never POST a draft?
 *
 * - `status === "complete"` (any kind: fixed, open-ended, bale, ended early,
 *   finished via the open-ended Finish) — the server's own word, and what the
 *   round response carries after the 60th confirm of an open-ended round.
 * - open-ended: `ends_done` at the `OPEN_ENDED_MAX_ENDS` cap (mirrors
 *   `backend/app/services/round_lifecycle.OPEN_ENDED_MAX_ENDS`, where the cap
 *   end auto-completes the round exactly like a fixed round's last planned
 *   end; belt-and-braces should the status lag).
 * - fixed: every planned end shot (`ends_done >= ends_total`).
 *
 * Release QA 2026-09-18 (sandbox round 492): the old open-ended branch could
 * never be "done", so a capped round rendered live scoring for "END 61", POSTed
 * an empty draft on load (409) and every confirm failed with "409 Round is
 * already complete".
 */
export function isRoundComplete(round) {
  if (!round) return false;
  if (round.status === "complete") return true;
  if (round.open_ended) return round.ends_done >= OPEN_ENDED_MAX_ENDS;
  return round.ends_done >= round.ends_total;
}

/**
 * Build the current-end progress label for a round: "End N" for an
 * open-ended round (no denominator — there is no plan to compare against),
 * "End N/M" for a fixed round.
 */
export function buildEndLabel(round) {
  return round.open_ended
    ? `End ${round.ends_done + 1}`
    : `End ${Math.min(round.ends_done + 1, round.ends_total)}/${round.ends_total}`;
}

/**
 * Build the sidebar card title for an active scoring session.
 * For league/event sessions with a week_number, returns "Wk N · End X/Y".
 * For plain practice sessions (no week_number), returns the event_name directly.
 */
export function buildActiveSessionTitle(round) {
  const endLabel = buildEndLabel(round);
  if (round.week_number != null) {
    return `Wk ${round.week_number} · ${endLabel}`;
  }
  return round.event_name || endLabel;
}

/**
 * useScoringSession — the live scoring session for one round (#2061).
 *
 * Owns the session state (`round`, `current`, `mode`, `editingEnd`,
 * `lastSaved`) plus everything the domain actions mutate on the way through:
 * the bale session, the #1903 waiting state, the autosave control refs, the
 * transient action error, the end-transition animation keys and the #1399
 * break-boundary cue. The two previously-extracted hooks (`useAutosave`,
 * `useBaleSession`) are consumed HERE and read their control refs from a single
 * session-owned `refs` bundle, so no caller hands them individual refs any more.
 *
 * The page keeps only presentation: sheet/disclosure toggles, the breakpoint
 * branch, membership-derived settings access, and the JSX.
 *
 * Behaviour is preserved verbatim from the pre-extraction Score.jsx — every
 * concurrency guard, ordering rule and early-exit is unchanged.
 *
 * @param {string|null} roundId              `?round=ID` from the URL, or null to
 *                                           resume the most recent active round.
 * @param {Function}    onActiveSessionChange Shell callback for the sidebar card.
 */
export function useScoringSession({ roundId: roundParam, onActiveSessionChange }) {
  const navigate = useNavigate();
  const { activeArcher, user } = useAuth();

  const [round, setRound] = useState(null);
  const [mode, setMode] = useState("drop");
  const [current, setCurrent] = useState({ arrows: [], positions: [] });
  const [actionError, setActionError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  // The end number being edited in place, or null when entering a new end.
  const [editingEnd, setEditingEnd] = useState(null);
  const [notes, setNotes] = useState("");
  // Bale session state — tracks other archers' draft progress for Next archer / Confirm end logic
  const [baleSession, setBaleSession] = useState(null);
  // #1903 — the end number whose bulk confirm was a silent no-op because this
  // device's round is AHEAD of its bale mates (the backend only commits rounds
  // at min_ends, by design). While set, a waiting banner explains why nothing
  // advanced; local arrows + the server draft are kept intact. Cleared when a
  // live bale update shows the mates caught up, on a real commit, and on round
  // load.
  const [baleWait, setBaleWait] = useState(null);
  // #1000 — missing useState declaration (was causing ReferenceError in confirmEndEarly)
  const [endEarlyOpen, setEndEarlyOpen] = useState(false); // eslint-disable-line no-unused-vars
  // #1009 — autosave failure banner
  const [autosaveWarning, setAutosaveWarning] = useState(false);

  // ── Scorer-mode derived values (early, before useEffect hooks) ──────────
  // These must be declared here so autosave effects can reference them.
  const { isScorerMode, isScorer, isViewer } = scoringRoles(baleSession, user);

  // ── Session-owned control refs ──────────────────────────────────────────
  // Bundled into one object so useAutosave / useBaleSession consume the SESSION
  // rather than a fistful of caller-owned refs. Identity is stable (all members
  // are refs), so no consumer dependency array is disturbed.
  //
  // liveRef            latest scoring state, for the interval safety-save closure.
  //                    roundParam is included so the SSE handler can check the
  //                    URL-based round ID even when `round` state still holds the
  //                    previous round during loading.
  // autosavePausedRef  autosave got a 404/409 — the round is gone or conflicted,
  //                    so stop retrying until a new round is loaded.
  // localChangeRef     set true the moment the user makes a local change
  //                    (add/undo/reposition), cleared when the autosave
  //                    completes. Suppresses live sync while the change is in
  //                    flight so server state can't race back over the edit.
  // confirmInFlightRef #1285 — true while an end is being confirmed. A trailing
  //                    debounced autosave for the just-confirmed end would
  //                    otherwise land after ends_done increments server-side and
  //                    get rejected with a 409 stale_end. Both autosave effects
  //                    check it and skip the POST. Reset when a fresh round loads.
  // lastSavedContentRef #1210 — serialized payload of the last *successful* save,
  //                    so an unchanged end never re-POSTs.
  // autosaveFailCountRef consecutive-failure counter driving the backoff.
  // initialBaleRedirectDone one-time flag for the on-load bale order check.
  const liveRef = useRef({ round: null, current, mode, editingEnd, baleSession: null, user: null, roundParam: null });
  const autosavePausedRef = useRef(false);
  const autosaveFailCountRef = useRef(0);
  const lastSavedContentRef = useRef(null);
  const localChangeRef = useRef(false);
  const confirmInFlightRef = useRef(false);
  const initialBaleRedirectDone = useRef(false);
  const refs = useMemo(
    () => ({
      liveRef,
      autosavePausedRef,
      autosaveFailCountRef,
      lastSavedContentRef,
      localChangeRef,
      confirmInFlightRef,
      initialBaleRedirectDone,
    }),
    [],
  );

  liveRef.current = { round, current, mode, editingEnd, baleSession, user, roundParam };

  // End confirmation animation state.
  const [endBarFlashKey, setEndBarFlashKey] = useState(0);
  // true = gold (end advanced), false = blue (Next Archer, same end)
  const [endBarFlashGold, setEndBarFlashGold] = useState(true);
  const [endAnimKey, setEndAnimKey] = useState(0);
  const [endOverlayKey, setEndOverlayKey] = useState(0);
  // Tracks the last "roundId:endNum" seen to detect arrivals and end advances.
  const prevTransitionKeyRef = useRef(null);

  // Break-boundary cue (#1399). A round is an ordered list of BREAKS (segments);
  // when the archer crosses into a new break we show a dismissible, NON-blocking
  // card with the running subtotal + any distance/face change + a "take a break"
  // prompt. `breakCue` holds the cue payload while shown; null when hidden. The
  // archer can continue scoring immediately — it never interrupts the flow.
  const [breakCue, setBreakCue] = useState(null);
  // Remember the last break boundary we showed a cue for, so it fires once per
  // boundary (not on every re-render or on a normal Next within a break).
  const shownBreakKeyRef = useRef(null);

  // ─── load the round to score ───
  // With `?round=ID`, resume exactly that round; otherwise pick the
  // archer's most recent in-progress round. Returns null when no active
  // round exists (signals redirect to /new-round).
  const { data: loadedRound, loading, error } = useApiData(
    () => {
      const pickRoundId = roundParam
        ? Promise.resolve(Number(roundParam))
        : api
            .me()
            .then((me) => api.rounds({ archer_id: me.id, active: true }))
            .then((list) => (list.length ? list[0].id : null));
      return pickRoundId.then((id) => (id == null ? null : api.round(id)));
    },
    [roundParam],
  );

  // ─── sync loaded round data into local state / handle redirect ───
  useEffect(() => {
    if (loading) return;
    if (loadedRound === null) {
      navigate("/new-round");
      return;
    }
    if (!loadedRound) return;
    setRound(loadedRound);
    setNotes(loadedRound.notes ?? "");
    // Always reset in-progress state when a new round loads — prevents
    // mid-end arrows from leaking into the next round when navigating via
    // the bale tray without confirming the current end first.
    setCurrent({ arrows: [], positions: [] });
    setEditingEnd(null);
    setLastSaved(null);
    setBaleWait(null);  // #1903 — waiting state belongs to the previous load
    // #1210 — a new round has its own draft history; clear the dedup key so the
    // first real edit always saves (and never matches the previous round's key).
    lastSavedContentRef.current = null;
    autosaveFailCountRef.current = 0;
    confirmInFlightRef.current = false;  // #1285 — fresh round starts with autosave enabled
    // Restore an autosaved in-progress end so a refresh loses nothing.
    if (loadedRound.draft && loadedRound.draft.arrows.length) {
      setCurrent({
        arrows: loadedRound.draft.arrows.map((a) => a.score),
        positions: loadedRound.draft.arrows.map((a) => ({ x: a.x, y: a.y })),
      });
      setLastSaved(parseUTC(loadedRound.draft.saved_at));
    }
  }, [loadedRound, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── tell the shell about the active session (sidebar card + topbar crumb) ───
  useEffect(() => {
    if (!round || !onActiveSessionChange) return;
    onActiveSessionChange({
      title: buildActiveSessionTitle(round),
      subtitle: `${(round.event_detail || "").toUpperCase()}`,
      eventName: round.event_name,
      kind: round.kind,
    });
    return () => onActiveSessionChange(null);
  }, [round, onActiveSessionChange]);

  // ─── autosave: per-arrow debounced save + periodic safety-timer save ───
  // The two near-duplicate autosave effects (with backoff, content dedup, and
  // the #1285 confirm-in-flight guard) live in hooks/useAutosave.
  useAutosave({
    round,
    // A completed round (fixed at its last end, open-ended at the cap or
    // finished) has no draft to keep and the server 409s
    // `round_already_complete` on every draft POST — never autosave it.
    roundComplete: isRoundComplete(round),
    current,
    mode,
    editingEnd,
    isViewer,
    refs,
    setAutosaveWarning,
    setLastSaved,
  });

  // ─── auto-dismiss transient action errors after 4 seconds ───
  useEffect(() => {
    if (!actionError) return undefined;
    const t = setTimeout(() => setActionError(null), 4000);
    return () => clearTimeout(t);
  }, [actionError]);

  // ─── bale session live sync (SSE + fallback poll, solo detection,
  //     auto-follow/navigate, initial redirect, scorer broadcast) ───
  // All of the bale-session machinery lives in hooks/useBaleSession. The
  // `baleSession` state is owned here (confirmEnd/nextArcher/skip mutate it too)
  // and passed in; the hook returns the derived `isSolo`.
  const { isSolo } = useBaleSession({
    round,
    baleSession,
    setBaleSession,
    user,
    isScorer,
    navigate,
    refs,
    setCurrent,
    setLastSaved,
    setRound,
  });

  // Total bale membership — registered scorer(s) PLUS any unregistered guest
  // archers, e.g. "bobby tables" scored on the host's device (sandbox QA fail
  // 2026-09-13, follow-up to item 1). Deliberately independent of `isSolo`
  // above: isSolo counts only REGISTERED archers because it gates whether
  // THIS DEVICE needs live-sync (SSE/poll) at all — a lone registered scorer
  // entering guest scores has no other device to sync with, so isSolo is
  // correctly true there. But a bale-wide action (end the round for
  // everyone) is about how many ROUNDS are on the bale, not how many devices
  // are watching it — a club scorer with one registered account and one
  // guest still has real bale-mates and must see that action.
  const baleArcherCount = baleSession?.archers?.length ?? 0;
  const hasBaleMates = baleArcherCount >= 2;

  // ─── #1903: auto-clear the confirm-while-ahead waiting state ──────────────
  // The waiting banner is raised when a bulk confirm was a no-op because this
  // round is ahead of its mates. Every live bale update (SSE push / poll)
  // lands in `baleSession`; once no active mate is still behind our local
  // ends_done, the wait is over — the next Confirm end will commit.
  useEffect(() => {
    if (baleWait == null || !baleSession || !round) return;
    if (!baleMatesStillBehind(baleSession, round)) setBaleWait(null);
  }, [baleWait, baleSession, round]);

  // arrow markers on the target = past ends (faded) + current end (bright).
  const targetArrows = useMemo(() => {
    if (!round) return [];
    const out = [];
    round.ends.forEach((e) => {
      e.arrows.forEach((a) => {
        if (a.x != null && a.y != null) {
          out.push({ x: a.x, y: a.y, n: e.end_number, ghost: true });
        }
      });
    });
    current.positions.forEach((p, i) => {
      // Keypad arrows carry no position (#1837) — skip them so a mixed end
      // never renders a phantom marker at centre. `idx` keeps the original
      // entry index so drag-relocate still targets the right arrow.
      if (p?.x != null && p?.y != null) {
        out.push({ x: p.x, y: p.y, n: i + 1, idx: i });
      }
    });
    return out;
  }, [round, current]);

  // In a bale session, the round the "Next archer" button would walk to — the
  // shared scores-for walk helper (#1898 round 3). Null when no other archer
  // this device scores for is still pending on the current end; the end-bar
  // button then falls back to "Confirm end" instead of offering a walk onto a
  // registered mate's round (QA FAIL 2: the unfiltered pending list kept the
  // "Next archer" label alive and tapping it cross-scored onto the mate's
  // card).
  const nextArcherWalkId = useMemo(() => {
    if (!round?.bale_session_id || !baleSession) return null;
    return pendingWalkRoundId(baleSession.archers, {
      endIdx: round.ends_done,
      arrowsPerEnd: round.arrows_per_end,
      currentRoundId: round.id,
      currentBalePosition: round.bale_position,
      excludeArcherId: round.archer_id,
      isScorer,
    });
  }, [baleSession, round, isScorer]);

  // Open-ended Simple Round (spec 2026-09-13): ends_total === ends_done at
  // every in-progress moment, so the fixed-round Math.min clamp collapses to
  // ends_done (off by one) — the next end is always ends_done + 1, uncapped
  // while in progress. Once the round is DONE (cap reached / status complete)
  // there is no next end: stay on the last one shot, exactly as the fixed
  // clamp does, instead of advertising a phantom "END 61" (QA 2026-09-18).
  const currentEndNumber = round
    ? (editingEnd != null
        ? editingEnd
        : round.open_ended
          ? (isRoundComplete(round) ? Math.max(round.ends_done, 1) : round.ends_done + 1)
          : Math.min(round.ends_done + 1, round.ends_total))
    : null;

  // ─── arrival-based animations ─────────────────────────────────────────────
  // End bar flash:  any arrival — gold when end advances, blue on Next Archer
  // Number spring:  end advance only (number didn't change on Next Archer)
  // Overlay card:   end advance only
  useEffect(() => {
    if (currentEndNumber === null) return;
    const key = `${round?.id}:${currentEndNumber}`;
    const prev = prevTransitionKeyRef.current;
    if (prev !== null && key !== prev) {
      const prevEndNum = Number(prev.split(':')[1]);
      const endAdvanced = currentEndNumber !== prevEndNum;
      setEndBarFlashKey(k => k + 1);
      setEndBarFlashGold(endAdvanced);
      if (endAdvanced) {
        setEndAnimKey(k => k + 1);
        setEndOverlayKey(k => k + 1);
      }
    }
    prevTransitionKeyRef.current = key;
  }, [round?.id, currentEndNumber]);

  // ─── break-boundary cue (#1399) ────────────────────────────────────────────
  // Fire when the current end is the FIRST end of a new break (sub-round) — on
  // EVERY break boundary, including same-distance breaks (WA Indoor 600). Skip
  // while editing an earlier end (editingEnd != null), and skip course rounds
  // (breakBoundaryInfo returns null — gated on the round's FACE, so a marked
  // course whose per-target segments carry distances never cues either;
  // sandbox QA 2026-09-18, round 488). Display-only: the cue is dismissible
  // and does NOT block scoring; the archer can keep going immediately.
  useEffect(() => {
    if (!round || currentEndNumber === null || editingEnd != null) return;
    const info = breakBoundaryInfo(round.segments, currentEndNumber, round.target_face);
    if (!info) return;
    const key = `${round.id}:${info.breakIndex}`;
    if (shownBreakKeyRef.current === key) return; // already shown for this boundary
    shownBreakKeyRef.current = key;
    setBreakCue(breakCuePayload(round, info));
  }, [round?.id, currentEndNumber, editingEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scorer-mode values that need bale data ──────────────────────────────
  const scorerName = isScorerMode
    ? ((baleSession.archers || []).find(a => a.user_id === baleSession.scorer_user_id)?.display_name ?? null)
    : null;
  const scorerCurrentRoundId = baleSession?.scorer_current_round_id ?? baleSession?.next_pending_round_id;
  // Include roundParam so the pill stays in FOLLOWING state during the brief
  // loading window after navigation fires but before the new round loads.
  const roundParamNum = roundParam ? Number(roundParam) : null;
  const isFollowingScorer = isViewer && (round?.id === scorerCurrentRoundId || roundParamNum === scorerCurrentRoundId);

  const arrowsPerEnd = round?.arrows_per_end;
  const endsDone = round?.ends_done;
  // One notion of "done" for fixed AND open-ended rounds (isRoundComplete):
  // drives the Close scoring end-bar, blocks score entry, and (via
  // useAutosave) suppresses every draft POST against a completed round.
  const allEndsDone = isRoundComplete(round);

  // Per-stage distance/face chips + the subtitle that goes with them.
  const { stageFace, stageDistance, eventDetailDisplay } = scoreStageDisplay(
    round,
    currentEndNumber,
  );

  // Show "Next archer" instead of "Confirm end" only when there is a pending
  // round this device may actually walk to (#1898 round 3). When the only
  // pending archers are registered mates finishing on their own devices, the
  // button stays "Confirm end" — the backend bulk confirm commits only
  // COMPLETE drafts, so confirming records this archer's end and leaves the
  // mates untouched.
  const isNextArcher = round?.bale_session_id != null && nextArcherWalkId != null && editingEnd == null;
  // X value = the face's top ring (maxArrowScoreForFace, the single JSON-backed
  // resolver, #1855) — on WA Field an X scores 6, matching the backend's
  // scoring_zones=6. This same value drives the multi-spot lowest-arrow
  // comparison, so wafield3spot compares X at 6 (backend parity).
  const scoringZones = maxArrowScoreForFace(round?.target_face);
  // Compound rounds use x_definition=ten_only — any 10 counts as an X. The round
  // response exposes inner_ten (compound ⟺ ten_only); prefer an explicit
  // x_definition if present, else derive from inner_ten. Recurve stays inner10.
  const xDefinition =
    round?.x_definition ?? (round?.inner_ten ? "ten_only" : "inner10");
  // Apply the multi-spot lowest-arrow downgrade to the in-progress end before
  // totalling, so the running total / X-count preview matches Confirm and the
  // review page (#1477). Only drop-mode arrows are grouped — keypad ("tap")
  // arrows carry no position (#1837) and pass spot=null (backend parity).
  const liveEffectiveArrows =
    round && isMultiSpot(round.target_face) && mode === "drop"
      ? applyMultiSpotRule(
          round.target_face,
          current.arrows,
          current.arrows.map((_, i) => {
            const p = current.positions[i];
            if (!p || typeof p.x !== "number" || typeof p.y !== "number") return null;
            return spotForPoint(round.target_face, toVbX(p.x), toVbY(p.y));
          }),
          scoringZones,
        )
      : current.arrows;
  const totalSoFar = round ? round.total + sumEnd(liveEffectiveArrows, scoringZones) : 0;
  const xSoFar = round ? round.x_count + countXForRound(liveEffectiveArrows, xDefinition) : 0;

  // Local score-entry operations for the in-progress end (placeArrow / pushScore /
  // relocateArrow / undoLast / applyInnerTen). See hooks/useScoreEntry.
  const { arrowIdx, placeArrow, pushScore, relocateArrow, undoLast } = useScoreEntry({
    round,
    current,
    setCurrent,
    localChangeRef,
    arrowsPerEnd,
    allEndsDone,
    editingEnd,
  });

  // Navigate to the next incomplete archer in the bale without confirming the end.
  async function nextArcher() {
    if (current.arrows.length !== arrowsPerEnd || submitting) return;
    setSubmitting(true);
    try {
      // Force-save draft before navigating so arrows are preserved for this archer.
      try { await api.saveDraft(round.id, arrowPayload(current), round.ends_done); } catch {}

      // Fetch fresh bale state — the 5s poll may be stale. If another device's
      // archer already submitted between polls, we need to confirm the end instead
      // of navigating away and leaving the bale session stuck.
      let freshBale = baleSession;
      try {
        freshBale = await api.getBaleSession(round.bale_session_id);
        setBaleSession(freshBale);
      } catch {}

      // Re-check the walk target with fresh data — the ONE shared scores-for
      // walk helper (#1898 round 3). The old code re-filtered pending archers
      // but picked the next one UNGATED, walking a registered user onto a
      // registered mate's round where subsequent taps cross-scored silently.
      const walkRoundId = pendingWalkRoundId(freshBale?.archers, {
        endIdx: round.ends_done,
        arrowsPerEnd,
        currentRoundId: round.id,
        currentBalePosition: round.bale_position,
        excludeArcherId: round.archer_id,
        isScorer,
      });

      if (walkRoundId == null) {
        // No round this device may walk to: either every archer has submitted,
        // or the only pending ones are registered mates finishing on their own
        // devices. Confirm instead of navigating away — the backend bulk
        // confirm only commits COMPLETE drafts, so it records this archer's
        // end and leaves any still-shooting mates untouched.
        try {
          const confirmRes = await api.confirmBaleSessionEnd(round.bale_session_id);
          const confirmedBale = await api.getBaleSession(round.bale_session_id);
          setBaleSession(confirmedBale);
          // #1903 — same silent-no-op guard as confirmEnd: when OUR round sat
          // ahead of min_ends the bulk confirm skipped it, so keep the local
          // end + server draft and surface the waiting state instead of
          // clearing (the reload below would otherwise wipe the entered end).
          if (!ownRoundCommitted(confirmRes, round.id, round.ends_done)) {
            setBaleWait(round.ends_done + 1);
            setSubmitting(false);
            return;
          }
          // Walk to the first in-play round this device scores for (same
          // scores-for policy as confirmEnd/confirmSkip, #1898): a registered
          // mate scores on their own device, so their round is never a target.
          const nextRoundId = nextWalkRoundId(confirmedBale?.archers, {
            currentRoundId: round.id,
            isScorer,
          });
          if (nextRoundId) {
            navigate('/score?round=' + nextRoundId);
            return;
          }
          // All archers on this bale are done (or we stay on our own round) — reload.
          const fresh = await api.round(round.id);
          setRound(fresh);
          setCurrent({ arrows: [], positions: [] });
          setLastSaved(null);
        } catch (e) {
          setActionError("Couldn't confirm end: " + e.message);
        }
        setSubmitting(false);
        return;
      }

      // A walkable archer is still pending — hand the device over to them.
      navigate('/score?round=' + walkRoundId);
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmEnd() {
    if (current.arrows.length !== arrowsPerEnd || submitting) return;
    setSubmitting(true);
    // #1285 — suppress any pending/queued autosave for the end being confirmed.
    // Once ends_done increments server-side, a trailing draft POST for this end
    // is rejected with 409 stale_end; cancelling it here avoids the wasted (and
    // occasionally user-visible) request. Cleared in finally and on round reload.
    confirmInFlightRef.current = true;
    try {
      let confirmedBale = null;
      if (round.bale_session_id && editingEnd == null) {
        // Bale mode: force-save current draft then bulk-confirm all pending ends.
        // #1285 — a stale_end 409 here means another device already advanced this
        // end; swallow it silently and proceed to confirm (idempotent), never
        // surfacing it as a visible "Couldn't save end" error.
        let forceSaved = null;
        try {
          forceSaved = await api.saveDraft(round.id, arrowPayload(current), round.ends_done);
        } catch (err) {
          if (!(err?.status === 409 && apiErrorCode(err) === "stale_end")) throw err;
        }
        try {
          confirmedBale = await api.confirmBaleSessionEnd(round.bale_session_id, baleSession?.version);
        } catch (confirmErr) {
          if (confirmErr?.status === 409 && apiErrorCode(confirmErr) === "stale_version") {
            const freshBale = confirmErr.body.detail.session;
            setBaleSession(freshBale);
            setCurrent({ arrows: [], positions: [] });
            setLastSaved(null);
            autosavePausedRef.current = false;
            setSubmitting(false);
            setActionError("End already confirmed by another device — refreshed");
            // #1898 — never trust the caller-agnostic next_pending_round_id
            // for navigation: derive the walk target under the scores-for
            // policy (registered mates score on their own devices).
            const nextRoundId = nextWalkRoundId(freshBale?.archers, {
              currentRoundId: round.id,
              isScorer,
            });
            if (nextRoundId) navigate('/score?round=' + nextRoundId);
            return;
          }
          throw confirmErr;
        }
        // #1903 — a 200 is NOT proof that OUR round committed: a device ahead
        // of its mates (e.g. after a skip) gets the session back unchanged.
        // Treating that as success cleared the local end and let the trailing
        // autosave POST an empty draft, wiping the server draft (a confirmed
        // data-loss window on reload).
        if (!ownRoundCommitted(confirmedBale, round.id, round.ends_done)) {
          // Keep EVERYTHING: local arrows stay on the card, the server draft
          // stays saved (the force-save above just re-persisted it), and the
          // dedup key is synced so no trailing autosave re-POSTs — least of
          // all an empty one. Surface a waiting state instead of silence.
          setBaleSession(confirmedBale);
          setBaleWait(currentEndNumber);
          lastSavedContentRef.current = autosaveContentKey(
            arrowPayload(current),
            round.ends_done,
          );
          if (forceSaved?.saved_at) setLastSaved(parseUTC(forceSaved.saved_at));
          return;
        }
        setBaleWait(null); // a real commit ends any earlier waiting state
      } else if (editingEnd != null) {
        // Editing an earlier end in place — later ends are kept and the
        // round's running totals recalculate on reload.
        await api.updateEnd(round.id, editingEnd, arrowPayload(current));
      } else {
        try {
          await api.confirmEnd(round.id, arrowPayload(current));
        } catch (e) {
          // #827: a 409 with code duplicate_end means the end was already
          // confirmed (duplicate request) — treat it as an idempotent success
          // and reload. Any OTHER 409 (e.g. open-ended's all_ends_shot at the
          // 60-end cap) must propagate so the arrows are NOT cleared and the
          // error surfaces via setActionError below.
          if (!(e?.status === 409 && apiErrorCode(e) === "duplicate_end")) throw e;
        }
      }
      // reload the round so totals/ends-done refresh
      const fresh = await api.round(round.id);
      setRound(fresh);

      // #1441 — confirming the next end auto-dismisses any break-boundary cue so
      // it never overlaps live scoring. Only on a real advance (not while editing
      // an earlier end). Safe against re-fire: shownBreakKeyRef still holds this
      // boundary's key, so the boundary effect won't re-raise the cue here; a new
      // cue only appears when the archer crosses into the NEXT break.
      if (editingEnd == null) setBreakCue(null);

      // Fire domain events for the end that was just confirmed.
      const endNum = editingEnd != null ? editingEnd : currentEndNumber;
      trackEvent("end_confirmed", {
        round_id: round.id,
        end_number: endNum,
        // Effective total (post multi-spot downgrade) — matches what was persisted.
        score: sumEnd(liveEffectiveArrows, scoringZones),
      });
      // Open-ended rounds have ends_total === ends_done after every confirmed
      // end (there is no plan to compare against) — round_completed for them
      // fires from finishRound, or HERE when the cap end auto-completes the
      // round server-side (the reloaded round carries `status: "complete"`;
      // isRoundComplete reads it). `setRound(fresh)` above already flipped the
      // page into the Close scoring state and silenced the autosave.
      if (isRoundComplete(fresh)) {
        trackEvent("round_completed", { round_id: round.id, ends_done: fresh.ends_done });
      }
      // After confirming a bale end, walk to the next round THIS DEVICE
      // scores for (#1898). The server's next_pending_round_id is
      // caller-agnostic (first in-play round in bale order, no notion of who
      // is asking) — navigating to it bounced a registered user onto their
      // registered mate's round, where subsequent taps cross-scored silently.
      // nextWalkRoundId applies the same scores-for policy as the skip paths
      // (#1897) and returns null to stay put when no legitimate target exists.
      if (round.bale_session_id && editingEnd == null && confirmedBale) {
        setBaleSession(confirmedBale);
        const nextRoundId = nextWalkRoundId(confirmedBale?.archers, {
          currentRoundId: round.id,
          isScorer,
        });
        if (nextRoundId) {
          setCurrent({ arrows: [], positions: [] });
          setLastSaved(null);
          setTimeout(() => navigate('/score?round=' + nextRoundId), 350);
          return;
        }
      }
      setCurrent({ arrows: [], positions: [] });
      // #<QA-3, sandbox sweep 2026-09-13> — editing an existing end has no
      // in-progress draft to clear (autosave is paused for the whole edit,
      // see useAutosave); the PUT above IS the save, so stamp lastSaved
      // instead of nulling it. Nulling here left `.sc-foot` reading "NOT YET
      // SAVED" for several seconds after a successful edit, until the NEXT
      // new end's autosave finally ran. A new (non-edit) confirm still nulls
      // it — there is genuinely no draft for the next, not-yet-started end.
      setLastSaved(editingEnd != null ? new Date() : null);
      setEditingEnd(null);
    } catch (e) {
      setActionError("Couldn't save end: " + e.message);
    } finally {
      setSubmitting(false);
      confirmInFlightRef.current = false;  // #1285 — re-enable autosave for the new end
    }
  }

  async function confirmSkip() {
    if (submitting || !skipReason.trim()) return;
    setSubmitting(true);
    try {
      await api.skipEnd(round.id, skipReason.trim());
      const fresh = await api.round(round.id);
      setRound(fresh);
      setCurrent({ arrows: [], positions: [] });
      setSkipOpen(false);
      setSkipReason("");
      setLastSaved(null);
      // In bale mode, navigate to the next archer still on the just-skipped end.
      if (round.bale_session_id) {
        try {
          const freshBale = await api.getBaleSession(round.bale_session_id);
          setBaleSession(freshBale);
          // Archers whose ends_completed equals the end count before this skip
          // are still ON the end we just skipped.
          const skippedEndIdx = fresh.ends_done - 1;
          const onSkippedEnd = (freshBale.archers || []).filter(
            a =>
              a.archer_id !== round.archer_id &&
              a.ends_completed === skippedEndIdx &&
              a.ends_completed < a.ends_total, // skip early-ended archers
          );
          // Archers who still need to SHOOT the skipped end — the shared
          // pending filter (#1889, LIVE-3): an archer with a complete draft is
          // done and just awaits confirmation, so navigating to them would
          // strand the view on the old end number.
          const pending = archersPendingOnEnd(freshBale.archers, {
            endIdx: skippedEndIdx,
            arrowsPerEnd,
            excludeArcherId: round.archer_id,
          });
          // Walk target for the skipped end — the ONE shared scores-for walk
          // helper (#1898 round 3). A device may only walk the bale to archers
          // it is responsible for scoring: everyone when we're the assigned
          // scorer, unregistered guests otherwise. A REGISTERED bale-mate
          // scores on their own device — yanking the skipper onto their round
          // re-shows the just-skipped end number (the reported bounce) and
          // even cross-saves drafts under the skipper's user id.
          const walkRoundId = pendingWalkRoundId(freshBale.archers, {
            endIdx: skippedEndIdx,
            arrowsPerEnd,
            currentRoundId: round.id,
            currentBalePosition: round.bale_position,
            excludeArcherId: round.archer_id,
            isScorer,
          });
          if (walkRoundId) {
            navigate('/score?round=' + walkRoundId);
            return;
          }
          if (pending.length > 0) {
            // Someone still needs to shoot the skipped end on their own
            // device — stay put; the header already advanced via
            // setRound(fresh) above. No premature bulk confirm.
            return;
          }
          // Nobody still needs to shoot the skipped end. If other archers are
          // still on it with complete drafts, confirm the bale end now
          // (mirrors nextArcher, #1889) so the whole bale advances with the
          // skipper instead of the session stalling on the old end.
          let baleNow = freshBale;
          if (onSkippedEnd.length > 0) {
            baleNow = await api.confirmBaleSessionEnd(round.bale_session_id);
            setBaleSession(baleNow);
          }
          // Everyone is past the skipped end — walk to the first in-play
          // round this device scores for, in bale order (the shared
          // post-confirm walk helper, #1898). When that's us (or a registered
          // mate on their own device), it returns null and we stay put: the
          // header already advanced via setRound(fresh) above (#1889).
          const nextRoundId = nextWalkRoundId(baleNow?.archers, {
            currentRoundId: round.id,
            isScorer,
          });
          if (nextRoundId) {
            navigate('/score?round=' + nextRoundId);
          }
        } catch {}
      }
    } catch (e) {
      setActionError("Couldn't skip end: " + e.message);
    } finally {
      setSubmitting(false);
    }
  }

  // Tap a confirmed end in the scorecard to edit it. The end is reopened
  // into the input panel; confirming saves it in place via updateEnd, so
  // every later end is preserved.
  function confirmEditEnd() {
    if (editTarget == null) return;
    // #847: Guard against overwriting in-progress arrows for the current end.
    // If the archer has already placed arrows in the current end, block the
    // edit and show a warning — they must confirm or undo their current end first.
    if (current.arrows.length > 0) {
      setActionError(
        `Confirm or undo your ${current.arrows.length} in-progress arrow${current.arrows.length !== 1 ? "s" : ""} before editing a past end.`,
      );
      setEditTarget(null);
      return;
    }
    const n = editTarget;
    const target = round.ends.find((e) => e.end_number === n);
    // #<QA-4, sandbox sweep 2026-09-13> — a skipped end's filler arrows carry
    // score "M" with x:null/y:null (method:"type"); restore them the same way
    // a keypad-entered end restores — score chips with no positions — instead
    // of wiping the buffer to force re-entry from scratch. Only an end with NO
    // arrows at all (defensive: legacy/malformed data) has nothing to pre-fill.
    const hasArrows = (target?.arrows?.length ?? 0) > 0;
    setCurrent({
      arrows: hasArrows ? target.arrows.map((a) => a.score) : [],
      positions: hasArrows ? target.arrows.map((a) => ({ x: a.x ?? null, y: a.y ?? null })) : [],
    });
    setEditingEnd(n);
    setEditTarget(null);
  }

  // Abandon an in-progress edit without changing the stored end.
  function cancelEndEdit() {
    setEditingEnd(null);
    setCurrent({ arrows: [], positions: [] });
  }

  // remaining: "remove" = keep what I shot (round shortens to the ends
  // actually shot); "misses" = unshot ends recorded as zeros (#1819).
  async function confirmEndEarly(remaining) {
    if (submitting || current.arrows.length > 0) return;
    setSubmitting(true);
    try {
      await api.endRoundEarly(round.id, remaining);
      trackEvent("round_completed", { round_id: round.id, ends_done: round.ends_done, early: true, remaining });
      navigate(`/rounds/${round.id}`);
    } catch (e) {
      setActionError("Couldn't finish round: " + e.message);
    } finally {
      setSubmitting(false);
      setEndEarlyOpen(false);
    }
  }

  // Ends EVERY in-progress member round on this bale session, then finalizes
  // it (sandbox QA sweep 2026-09-13, item 1) — the whole-bale counterpart to
  // confirmEndEarly above, which only ever touches this archer's own round.
  // The bale endpoint's `remaining` contract is "remove" | "zero", unlike the
  // per-round endpoint's "remove" | "misses" — the shared confirm step's
  // "Score them as misses" choice is translated accordingly so one UI step
  // can drive either API.
  async function confirmBaleEndEarly(remaining) {
    if (submitting || current.arrows.length > 0 || round.bale_session_id == null) return;
    setSubmitting(true);
    try {
      const apiRemaining = remaining === "misses" ? "zero" : remaining;
      await api.endBaleSessionEarly(round.bale_session_id, apiRemaining);
      trackEvent("round_completed", {
        round_id: round.id,
        ends_done: round.ends_done,
        early: true,
        remaining: apiRemaining,
        bale: true,
      });
      navigate(`/rounds/${round.id}`);
    } catch (e) {
      setActionError("Couldn't finish round: " + e.message);
    } finally {
      setSubmitting(false);
      setEndEarlyOpen(false);
    }
  }

  // Open-ended Simple Round (spec 2026-09-13): finish at the ends scored.
  async function finishRound() {
    if (submitting || current.arrows.length > 0) return;
    setSubmitting(true);
    try {
      await api.finishRound(round.id);
      trackEvent("round_completed", { round_id: round.id, ends_done: round.ends_done, open_ended: true });
      navigate(`/rounds/${round.id}`);
    } catch (e) {
      setActionError("Couldn't finish round: " + e.message);
    } finally {
      setSubmitting(false);
      setEndEarlyOpen(false);
    }
  }

  // Viewer "Follow" button — jump to the round the scorer is currently on.
  // Only reachable while scorerCurrentRoundId != null (the button that calls it
  // is rendered under that condition).
  function followScorer() {
    navigate('/score?round=' + scorerCurrentRoundId);
  }

  // Close scoring — mark bale session complete and navigate to round review.
  async function handleCloseScoring() {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (round.bale_session_id) {
        // Only ask the server to finalize when every bale-mate is done —
        // closing with an unfinished sibling round is refused server-side
        // (409 rounds_incomplete, #1889): an early finisher just exits their
        // own view while the session stays active for the others.
        const everyoneDone = (baleSession?.archers || []).every(
          a => a.ends_completed >= a.ends_total,
        );
        if (everyoneDone) {
          await api.closeBaleSession(round.bale_session_id);
        }
      }
    } catch {
      // continue to navigate even if close fails
    } finally {
      setSubmitting(false);
    }
    // Find the active user's own round in the bale session.
    // round.id may be another archer's round if bale auto-navigation moved us there.
    let dest = round.id;
    if (baleSession && activeArcher) {
      const mine = (baleSession.archers || []).find(a => a.archer_id === activeArcher.id);
      if (mine?.round_id) dest = mine.round_id;
    }
    navigate(`/rounds/${dest}`);
  }

  return {
    // ── load status ──
    loading,
    error,

    // ── session state ──
    round,
    setRound,
    current,
    mode,
    setMode,
    editingEnd,
    lastSaved,
    notes,
    setNotes,

    // ── bale + role ──
    baleSession,
    baleWait,
    setBaleWait,
    isSolo,
    hasBaleMates,
    baleArcherCount,
    isScorerMode,
    isViewer,
    scorerName,
    scorerCurrentRoundId,
    isFollowingScorer,

    // ── transient UI feedback the actions own ──
    submitting,
    actionError,
    setActionError,
    autosaveWarning,
    breakCue,
    setBreakCue,
    skipOpen,
    setSkipOpen,
    skipReason,
    setSkipReason,
    editTarget,
    setEditTarget,

    // ── end-transition animation keys ──
    endBarFlashKey,
    endBarFlashGold,
    endAnimKey,
    endOverlayKey,

    // ── derived scoring values ──
    arrowsPerEnd,
    endsDone,
    allEndsDone,
    currentEndNumber,
    targetArrows,
    liveEffectiveArrows,
    totalSoFar,
    xSoFar,
    isNextArcher,
    stageFace,
    stageDistance,
    eventDetailDisplay,

    // ── actions ──
    arrowIdx,
    placeArrow,
    pushScore,
    relocateArrow,
    undoLast,
    nextArcher,
    confirmEnd,
    confirmSkip,
    confirmEditEnd,
    cancelEndEdit,
    confirmEndEarly,
    confirmBaleEndEarly,
    finishRound,
    followScorer,
    handleCloseScoring,
  };
}
